import { useEffect, useState } from "react";
import api, { mediaUrl, formatError } from "../lib/api";
import { toast } from "sonner";
import { MultiProductPicker } from "../components/MultiProductPicker";
import { shareMediaFile } from "../lib/share";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Wand2, Loader2, Sparkles, Tv, Check, Clock, Timer, Send, Trash2, Plus, ImageUp, Share2, Download, Copy, X,
} from "lucide-react";

// Product-ad style presets (Google Flow–style). {product} is replaced with the product name/desc.
export const AD_PRESET_SUFFIX = "Preserve the exact product: keep the logo, label text, texture, colors and proportions unchanged. Only change the environment, lighting, and motion. Vertical 9:16.";
export const AD_PRESETS = [
  { key: "smoke", cmd: "/smoke", label: "Smoke Reveal", template: "Dramatic dark studio, thick smoke clears to reveal {product} centered, rim lighting, cinematic." },
  { key: "3d", cmd: "/3d", label: "3D Fluid Splash", template: "{product} with a dynamic 3D liquid and ice-cube splash around it, frozen mid-motion, droplets, high-speed macro look." },
  { key: "pov", cmd: "/pov", label: "POV Product Usage", template: "First-person POV of a hand taking {product} from a fridge in a home kitchen and pouring it into an ice-filled glass." },
  { key: "miniature", cmd: "/miniature", label: "Miniature World", template: "Macro hero shot of {product} surrounded by a miniature landscape built from its ingredients (e.g. fruit slices), with a droplet impact." },
  { key: "tiny", cmd: "/tiny", label: "Tiny Workers", template: "Miniature diorama figurine workers tightening bolts on and opening {product}, tilt-shift look." },
  { key: "timesquare", cmd: "/timesquare", label: "Times Square 3D", template: "Realistic Times Square at night, with {product} bursting out of a giant billboard screen in an anamorphic 3D illusion that breaks the screen frame." },
];
export const buildAdPrompts = (presetKey, product) => {
  const p = AD_PRESETS.find((x) => x.key === presetKey);
  if (!p) return null;
  const prod = (product || "").trim() || "the product";
  const base = p.template.replace(/\{product\}/g, prod);
  return { label: p.label, direction: base, videoPrompt: `${base} ${AD_PRESET_SUFFIX}` };
};

const AI_STYLES = [
  { v: "fresh", label: "🥬 Fresh & Wholesome" },
  { v: "bold", label: "🔥 Bold Sale Poster" },
  { v: "elegant", label: "✨ Upscale / Gourmet" },
  { v: "festive", label: "🎊 Festive" },
];

const PRESETS = [
  { label: "1 day", days: 1 },
  { label: "3 days", days: 3 },
  { label: "1 week", days: 7 },
  { label: "2 weeks", days: 14 },
];

function GenerateDialog({ onClose, onSaved }) {
  const [mode, setMode] = useState("scene"); // 'scene' | 'product'
  const [prompt, setPrompt] = useState("");
  const [scene, setScene] = useState("");
  const [direction, setDirection] = useState("");
  const [headline, setHeadline] = useState("");
  const [offer, setOffer] = useState("");
  const [style, setStyle] = useState("fresh");
  const [quality, setQuality] = useState("premium");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [products, setProducts] = useState([]);
  const [adPresets, setAdPresets] = useState([]); // selected preset keys (max 6)
  const [presetQuery, setPresetQuery] = useState("");

  const togglePreset = (key) => {
    setAdPresets((prev) => prev.includes(key) ? prev.filter((k) => k !== key)
      : (prev.length >= 6 ? prev : [...prev, key]));
  };
  const slashOpen = presetQuery.startsWith("/");
  const filteredPresets = AD_PRESETS.filter((p) => {
    const q = presetQuery.slice(1).toLowerCase();
    return !q || p.label.toLowerCase().includes(q) || p.cmd.includes(presetQuery.toLowerCase());
  });

  const generate = async () => {
    setBusy(true);
    setResult(null);
    try {
      let data;
      if (mode === "product") {
        if (products.length === 0) { setBusy(false); return toast.error("Upload at least one product photo"); }
        // Ad-style presets: one slide per selected preset, from the product image + preset template.
        if (adPresets.length > 0) {
          const productName = products[0]?.desc?.trim() || headline.trim() || "the product";
          let made = 0, last = null;
          for (const key of adPresets) {
            const ap = buildAdPrompts(key, productName);
            const fd = new FormData();
            products.forEach((p) => { fd.append("files", p.file); fd.append("descriptions", p.desc || ""); });
            fd.append("headline", headline.trim());
            fd.append("offer", offer.trim());
            fd.append("style", style);
            fd.append("scene", scene.trim());
            fd.append("direction", ap.direction);
            fd.append("quality", quality);
            fd.append("video_prompt", ap.videoPrompt);
            fd.append("ad_preset", ap.label);
            try {
              const resp = await api.post("/media/generate-product-slide", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 240000 });
              last = resp.data; made += 1; onSaved();
            } catch (e) {
              toast.error(`${ap.label}: ${formatError(e.response?.data?.detail) || "failed"}`);
            }
          }
          setResult(last);
          setBusy(false);
          if (made) toast.success(`${made} ad slide${made > 1 ? "s" : ""} created — each has a Video prompt to copy`);
          return;
        }
        const fd = new FormData();
        products.forEach((p) => { fd.append("files", p.file); fd.append("descriptions", p.desc || ""); });
        fd.append("headline", headline.trim());
        fd.append("offer", offer.trim());
        fd.append("style", style);
        fd.append("scene", scene.trim());
        fd.append("direction", direction.trim());
        fd.append("quality", quality);
        ({ data } = await api.post("/media/generate-product-slide", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 240000 }));
      } else {
        if (!prompt.trim()) { setBusy(false); return toast.error("Describe what the slide should show"); }
        ({ data } = await api.post("/media/generate-ai", {
          prompt: prompt.trim(), headline: headline.trim(), offer: offer.trim(), style, quality, direction: direction.trim(),
        }, { timeout: 240000 }));
      }
      setResult(data);
      onSaved();
      toast.success("Slide generated with your logo & branding");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Generation failed");
    }
    setBusy(false);
  };

  return (
    <DialogContent className="max-w-3xl w-[calc(100%-1.5rem)] max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="text-lg md:text-xl pr-6 flex items-center gap-2"><Wand2 className="w-5 h-5 text-primary" /> Generate an ad slide with AI</DialogTitle>
        <DialogDescription className="sr-only">Design a branded 1920x1080 ad slide, from a description or from your own product photo.</DialogDescription>
      </DialogHeader>

      {/* mode toggle */}
      <div className="grid grid-cols-2 gap-2 p-1 rounded-lg bg-secondary/50 border border-border">
        <button type="button" onClick={() => setMode("scene")} data-testid="mode-scene-btn"
          className={`flex items-center justify-center gap-2 rounded-md py-2 text-sm font-medium transition-colors ${mode === "scene" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
          <Wand2 className="w-4 h-4" /> Describe a scene
        </button>
        <button type="button" onClick={() => setMode("product")} data-testid="mode-product-btn"
          className={`flex items-center justify-center gap-2 rounded-md py-2 text-sm font-medium transition-colors ${mode === "product" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
          <ImageUp className="w-4 h-4" /> Use my product photo
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div className="space-y-3">
          {mode === "scene" ? (
            <div>
              <Label>What should the slide show?</Label>
              <Input value={prompt} onChange={(e) => setPrompt(e.target.value)} className="mt-1"
                placeholder="Fresh mangoes on a wooden table, bright produce look" data-testid="ai-prompt-input" />
            </div>
          ) : (
            <div>
              <MultiProductPicker items={products} setItems={setProducts} max={4} />
              <p className="text-[11px] text-muted-foreground mt-1">Add up to <b>4</b> products/characters — the AI designs one premium ad featuring them all (kept exactly). Describe each so the AI knows what's what.</p>
              <div className="mt-2">
                <Label>Background scene (optional)</Label>
                <Input value={scene} onChange={(e) => setScene(e.target.value)} className="mt-1"
                  placeholder="warm rustic kitchen counter, soft morning light" data-testid="ai-scene-input" />
              </div>
              {/* Ad Style presets — one slide per selected style, from the product image */}
              <div className="mt-3">
                <Label className="flex items-center gap-1.5">🎬 Ad Style presets <span className="text-[10px] font-normal text-muted-foreground">(optional — one slide each, up to 6)</span></Label>
                <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="ad-preset-chips">
                  {AD_PRESETS.map((p) => {
                    const on = adPresets.includes(p.key);
                    return (
                      <button type="button" key={p.key} onClick={() => togglePreset(p.key)} data-testid={`ad-preset-${p.key}`}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "bg-secondary border-border text-muted-foreground hover:text-foreground"}`}>
                        {on && <Check className="w-3 h-3 mr-1 inline -mt-0.5" />}{p.label}
                      </button>
                    );
                  })}
                </div>
                <div className="relative mt-1.5">
                  <Input value={presetQuery} onChange={(e) => setPresetQuery(e.target.value)}
                    placeholder='Type "/" to pick a style (e.g. /smoke)' className="text-xs h-8" data-testid="ad-preset-input" />
                  {slashOpen && filteredPresets.length > 0 && (
                    <div className="absolute z-20 left-0 right-0 mt-1 rounded-md border border-border bg-popover shadow-lg overflow-hidden" data-testid="ad-preset-dropdown">
                      {filteredPresets.map((p) => (
                        <button type="button" key={p.key} onClick={() => { togglePreset(p.key); setPresetQuery(""); }}
                          className="w-full text-left px-3 py-1.5 text-xs hover:bg-secondary flex items-center justify-between">
                          <span><b>{p.cmd}</b> — {p.label}</span>
                          {adPresets.includes(p.key) && <Check className="w-3 h-3 text-primary" />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {adPresets.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid="ad-preset-tags">
                    {adPresets.map((k) => {
                      const p = AD_PRESETS.find((x) => x.key === k);
                      return (
                        <span key={k} className="text-[11px] bg-primary/15 text-primary border border-primary/30 rounded-full pl-2 pr-1 py-0.5 flex items-center gap-1">
                          {p.label} x1
                          <button type="button" onClick={() => togglePreset(k)} className="hover:bg-primary/20 rounded-full p-0.5" data-testid={`ad-preset-remove-${k}`}><X className="w-3 h-3" /></button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
          <div>
            <Label>Headline (big text)</Label>
            <Input value={headline} onChange={(e) => setHeadline(e.target.value)} className="mt-1"
              placeholder="Weekly Deals" data-testid="ai-headline-input" />
          </div>
          <div>
            <Label>Price / offer (optional)</Label>
            <Input value={offer} onChange={(e) => setOffer(e.target.value)} className="mt-1"
              placeholder="Only $1.49 / lb" data-testid="ai-offer-input" />
          </div>
          <div>
            <Label className="flex items-center gap-1.5">🎬 Direction &amp; mood <span className="text-[10px] font-normal text-muted-foreground">(shapes the look — never printed on the slide)</span></Label>
            <Textarea value={direction} onChange={(e) => setDirection(e.target.value)} rows={3} className="mt-1 resize-none" data-testid="ai-direction-input"
              placeholder="Describe the feeling you want, e.g. “steamy hot dish fresh out of the oven, glistening, dramatic close-up, warm cinematic light — make people drool.”" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Style</Label>
              <Select value={style} onValueChange={setStyle}>
                <SelectTrigger className="mt-1" data-testid="ai-style-select"><SelectValue /></SelectTrigger>
                <SelectContent>{AI_STYLES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Quality</Label>
              <Select value={quality} onValueChange={setQuality}>
                <SelectTrigger className="mt-1" data-testid="ai-quality-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="premium">✨ Premium (best)</SelectItem>
                  <SelectItem value="fast">Fast</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={generate} disabled={busy} className="w-full" data-testid="ai-generate-btn">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {mode === "product" ? "Enhancing & designing…" : "Designing your slide…"}</>
              : <><Wand2 className="w-4 h-4 mr-2" /> {result ? "Regenerate" : "Generate slide"}</>}
          </Button>
          <p className="text-[11px] text-muted-foreground">Your <b className="text-primary">Market Place On Oakton</b> logo is placed at the top and a <b>SignHub · Powered by AV Tech</b> credit at the bottom automatically.</p>
        </div>
        <div>
          <Label>Preview (1920×1080)</Label>
          <div className="mt-1 aspect-video rounded-lg overflow-hidden border border-border bg-black flex items-center justify-center" data-testid="ai-preview">
            {busy ? <div className="text-center text-muted-foreground text-sm px-4"><Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin text-primary" />Astra is designing…</div>
              : result ? <img src={mediaUrl(result.id)} alt="slide" className="w-full h-full object-contain" />
              : <div className="text-center text-muted-foreground/60 text-sm px-4"><Wand2 className="w-8 h-8 mx-auto mb-2 opacity-40" />Your branded slide appears here</div>}
          </div>
          {result && <p className="text-[11px] text-green-400 mt-2">✓ Saved — close and push it to your TVs below.</p>}
        </div>
      </div>
      <DialogFooter className="mt-2">
        <Button variant="secondary" onClick={onClose}>{result ? "Done" : "Cancel"}</Button>
      </DialogFooter>
    </DialogContent>
  );
}

export function PushDialog({ slide, tvs, onClose, onPushed }) {
  const [allTvs, setAllTvs] = useState(true);
  const [selected, setSelected] = useState({});
  const [every, setEvery] = useState(3);
  const [useTimer, setUseTimer] = useState(false);
  const [expiryMode, setExpiryMode] = useState("preset");
  const [presetDays, setPresetDays] = useState(7);
  const [customDt, setCustomDt] = useState("");
  const [saving, setSaving] = useState(false);

  const computeExpiry = () => {
    if (!useTimer) return null;
    if (expiryMode === "preset") return new Date(Date.now() + presetDays * 86400000).toISOString();
    if (!customDt) return "invalid";
    return new Date(customDt).toISOString();
  };
  const endLabel = () => {
    if (!useTimer) return "Runs until you stop it";
    const iso = computeExpiry();
    if (!iso || iso === "invalid") return "—";
    return new Date(iso).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  };

  const push = async () => {
    const expires_at = computeExpiry();
    if (useTimer && (expires_at === "invalid" || !expires_at)) return toast.error("Set an expiry date & time");
    if (useTimer && new Date(expires_at).getTime() <= Date.now()) return toast.error("Expiry must be in the future");
    const tv_ids = allTvs ? null : Object.keys(selected).filter((k) => selected[k]);
    if (!allTvs && (!tv_ids || tv_ids.length === 0)) return toast.error("Pick at least one TV");
    setSaving(true);
    try {
      await api.post("/ads", {
        media_id: slide.id, tv_ids, every: Number(every),
        expires_at: useTimer ? expires_at : null,
        name: slide.original_filename,
      });
      toast.success(useTimer ? "📢 Slide is live — it auto-removes when the timer ends" : "📢 Slide is live on your TVs");
      onPushed();
      onClose();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not push slide");
    }
    setSaving(false);
  };

  return (
    <DialogContent className="max-w-2xl w-[calc(100%-1.5rem)] max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="text-lg md:text-xl pr-6 flex items-center gap-2"><Send className="w-5 h-5 text-primary" /> Push slide to TVs</DialogTitle>
        <DialogDescription className="sr-only">Choose which TVs show this AI slide and optionally set an auto-expiry timer.</DialogDescription>
      </DialogHeader>

      <div className="space-y-5">
        <div className="aspect-video rounded-lg overflow-hidden border border-border bg-black max-h-48 mx-auto">
          {slide.type === "video" ? (
            <video src={mediaUrl(slide.id)} className="w-full h-full object-contain" muted loop autoPlay playsInline />
          ) : (
            <img src={mediaUrl(slide.id)} alt="" className="w-full h-full object-contain" />
          )}
        </div>

        {/* TVs */}
        <div>
          <Label>1. Which TVs show this slide?</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            <button type="button" onClick={() => { setAllTvs(true); setSelected({}); }} data-testid="push-tv-all"
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${allTvs ? "border-primary bg-primary/10 text-primary" : "border-border bg-secondary/40 hover:border-muted-foreground"}`}>
              <Check className={`w-3.5 h-3.5 inline mr-1 ${allTvs ? "" : "opacity-0"}`} />All TVs
            </button>
            {tvs.map((tv) => {
              const on = !allTvs && selected[tv.id];
              return (
                <button key={tv.id} type="button" data-testid={`push-tv-${tv.id}`}
                  onClick={() => setSelected((s) => { const ns = { ...s, [tv.id]: !s[tv.id] }; setAllTvs(false); return ns; })}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors flex items-center gap-1.5 ${on ? "border-primary bg-primary/10 text-primary" : "border-border bg-secondary/40 hover:border-muted-foreground"}`}>
                  <Tv className="w-3.5 h-3.5" /> {tv.name}{on && <Check className="w-3.5 h-3.5" />}
                </button>
              );
            })}
            {tvs.length === 0 && <p className="text-sm text-muted-foreground py-1">No TVs paired yet.</p>}
          </div>
        </div>

        {/* Frequency */}
        <div className="flex items-center justify-between gap-3">
          <Label>Show it every</Label>
          <Select value={String(every)} onValueChange={(v) => setEvery(Number(v))}>
            <SelectTrigger className="w-40" data-testid="push-every-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[2, 3, 4, 5, 6, 8].map((n) => <SelectItem key={n} value={String(n)}>every {n} slides</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <p className="text-[11px] text-amber-400/90 -mt-3">The slide rotates in with your playlist/offers — needs content running on the TV.</p>

        {/* Timer */}
        <div className="rounded-lg border border-border p-3">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-1.5 cursor-pointer"><Timer className="w-4 h-4" /> 2. Auto-expire timer</Label>
            <Switch checked={useTimer} onCheckedChange={setUseTimer} data-testid="push-timer-toggle" />
          </div>
          {useTimer ? (
            <>
              <div className="flex flex-wrap gap-2 mt-3">
                {PRESETS.map((p) => (
                  <button type="button" key={p.days} data-testid={`push-preset-${p.days}`}
                    onClick={() => { setExpiryMode("preset"); setPresetDays(p.days); }}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${expiryMode === "preset" && presetDays === p.days ? "border-primary bg-primary/10 text-primary" : "border-border bg-secondary/40 hover:border-muted-foreground"}`}>
                    {p.label}
                  </button>
                ))}
                <button type="button" data-testid="push-preset-custom"
                  onClick={() => setExpiryMode("custom")}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${expiryMode === "custom" ? "border-primary bg-primary/10 text-primary" : "border-border bg-secondary/40 hover:border-muted-foreground"}`}>
                  Custom date & time
                </button>
              </div>
              {expiryMode === "custom" && (
                <Input type="datetime-local" value={customDt} min={new Date().toISOString().slice(0, 16)}
                  onChange={(e) => setCustomDt(e.target.value)} className="mt-2" data-testid="push-custom-datetime" />
              )}
            </>
          ) : (
            <p className="text-[11px] text-muted-foreground mt-2">Timer off — the slide keeps showing until you stop it from the Timed Ads page.</p>
          )}
          <p className="text-sm mt-3 flex items-center gap-1.5" data-testid="push-ends-label">
            <Clock className="w-4 h-4 text-primary" /> {useTimer ? "Ends:" : "Duration:"} <b className="text-primary">{endLabel()}</b>
          </p>
        </div>
      </div>

      <DialogFooter className="mt-2">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={push} disabled={saving} data-testid="push-confirm-btn">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Pushing…</> : <><Send className="w-4 h-4 mr-2" /> Push to TVs</>}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

export default function AiSlides() {
  const [media, setMedia] = useState([]);
  const [tvs, setTvs] = useState([]);
  const [genOpen, setGenOpen] = useState(false);
  const [pushSlide, setPushSlide] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const copyVideoPrompt = async (m) => {
    try {
      await navigator.clipboard.writeText(m.video_prompt || "");
      setCopiedId(m.id);
      toast.success("Video prompt copied — paste it into Google Flow");
      setTimeout(() => setCopiedId((c) => (c === m.id ? null : c)), 2000);
    } catch { toast.error("Couldn't copy"); }
  };

  const load = () => api.get("/media").then((r) => {
    if (Array.isArray(r.data)) setMedia((prev) => (r.data.length === 0 && prev.length > 0 ? prev : r.data));
  }).catch(() => {});
  useEffect(() => {
    load();
    api.get("/tvs").then((r) => setTvs(r.data)).catch(() => {});
  }, []);

  const remove = async (m) => {
    if (!window.confirm(`Delete "${m.original_filename}"?`)) return;
    try { await api.delete(`/media/${m.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  const slides = media.filter((m) => m.ai_generated && !m.is_clip);

  return (
    <div className="p-4 md:p-8 pb-24 md:pb-8 max-w-5xl">
      <div className="mb-5 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl md:text-3xl font-display font-bold tracking-tight flex items-center gap-2">
            <Wand2 className="w-6 h-6 text-primary" /> AI Slide Generator
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Design branded 1920×1080 ad slides with AI, then push them to any TV — with an optional auto-expiry timer.
          </p>
        </div>
        <Button className="flex-shrink-0" onClick={() => setGenOpen(true)} data-testid="new-slide-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New Slide
        </Button>
      </div>

      {slides.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center" data-testid="ai-slides-empty">
          <Sparkles className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" />
          <p className="text-muted-foreground">No AI slides yet. Click <b>New Slide</b> to design one — your logo is added automatically.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ai-slides-list">
          {slides.map((m) => (
            <div key={m.id} className="rounded-xl border border-border bg-card overflow-hidden fade-up" data-testid={`ai-slide-${m.id}`}>
              <div className="aspect-video bg-black">
                <img src={mediaUrl(m.id)} alt={m.original_filename} className="w-full h-full object-cover" />
              </div>
              <div className="p-3 space-y-2">
                <p className="font-semibold text-sm truncate">{m.original_filename}</p>
                {m.video_prompt && (
                  <div className="rounded-md bg-secondary/60 border border-border p-2" data-testid={`video-prompt-${m.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">🎬 Video prompt{m.ad_preset ? ` · ${m.ad_preset}` : ""}</span>
                      <button type="button" onClick={() => copyVideoPrompt(m)} className="text-[11px] text-primary hover:underline flex items-center gap-1" data-testid={`copy-video-prompt-${m.id}`}>
                        <Copy className="w-3 h-3" /> {copiedId === m.id ? "Copied" : "Copy"}
                      </button>
                    </div>
                    <p className="text-[11px] text-muted-foreground line-clamp-3">{m.video_prompt}</p>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Button size="sm" className="flex-1 h-8 text-xs" onClick={() => setPushSlide(m)} data-testid={`push-slide-${m.id}`}>
                    <Send className="w-3.5 h-3.5 mr-1.5" /> Push to TVs
                  </Button>
                  <Button size="sm" variant="secondary" className="h-8 px-2" onClick={() => shareMediaFile(m)} data-testid={`share-slide-${m.id}`} title="Send as photo to WhatsApp/Instagram">
                    <Share2 className="w-3.5 h-3.5" />
                  </Button>
                  <a href={mediaUrl(m.id)} download={m.original_filename} target="_blank" rel="noreferrer"
                    className="h-8 px-2 inline-flex items-center justify-center rounded-md border border-border bg-secondary hover:bg-secondary/70 transition-colors"
                    data-testid={`download-slide-${m.id}`} title="Download / open the slide">
                    <Download className="w-3.5 h-3.5" />
                  </a>
                  <Button size="sm" variant="ghost" className="h-8 px-2 text-muted-foreground hover:text-destructive"
                    onClick={() => remove(m)} data-testid={`delete-slide-${m.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        {genOpen && <GenerateDialog onClose={() => setGenOpen(false)} onSaved={load} />}
      </Dialog>
      <Dialog open={!!pushSlide} onOpenChange={(o) => !o && setPushSlide(null)}>
        {pushSlide && <PushDialog slide={pushSlide} tvs={tvs} onClose={() => setPushSlide(null)} onPushed={() => {}} />}
      </Dialog>
    </div>
  );
}
